<script setup lang="ts">
/**
 * DonateExpressCheckoutSection — bloc des paiements express en haut de la
 * colonne formulaire des pages de don.
 *
 * Comportement (parité totale /c /donate /a /b /pay, Karim 2026-06-21) :
 *  - **preferred = stripe** OU (Stripe-only) →
 *      Apple Pay + Google Pay via **Stripe Express Checkout** (1 container)
 *      + bouton **PayPal alternative** si PayPal aussi configuré
 *  - **preferred = paypal** OU (PayPal-only) →
 *      Apple Pay + Google Pay via **PayPal Applepay/Googlepay** (2 containers
 *      séparés) + bouton **PayPal jaune** (le primaire) en troisième
 *
 * Pourquoi cette séparation : le SDK Stripe Express Checkout regroupe
 * Apple+Google dans un seul element. Le SDK PayPal expose Applepay() et
 * Googlepay() comme 2 builders séparés — chacun mountable sur son propre
 * container DOM. On reflète cette structure côté UI.
 *
 * Le composant ne MOUNT PAS lui-même les boutons : il fournit juste les
 * conteneurs DOM avec les bons IDs. La page parente (qui a accès à
 * stripeCheckout/paypalCheckout) appelle `mountExpressCheckout`,
 * `mountApplePay`, `mountGooglePay`, `mountExpressCheckout` PayPal
 * (Smart Buttons) au bon moment via onMounted.
 *
 * Visibilité globale du bloc :
 *  - Caché si subscription Stripe one-time-only (Express Checkout Stripe
 *    ne supporte pas le récurrent, mais PayPal-only récurrent OK).

 */
import { computed, type ComputedRef, type Ref } from "vue";
import type { useCheckout } from "@donoor/checkout";

interface PaymentFormSlice {
  isSubscription: Ref<boolean> | ComputedRef<boolean>;
}

interface Props {
  /** Instance Stripe (useStripeDonationCheckout().checkout). */
  stripeCheckout: ReturnType<typeof useCheckout>;
  /** Instance PayPal (usePaypalDonationCheckout().checkout). */
  paypalCheckout: ReturnType<typeof useCheckout>;
  /** Composable de form Donoor — re-mount des boutons PayPal au change subscription. */
  paymentForm: PaymentFormSlice;
  /** Mode A : asso PayPal-only (Stripe pas configuré). */
  isPaypalOnlyMode: boolean;
  /** Mode B : asso Stripe + PayPal cohabitent → PayPal en alternative. */
  showPayPalAlternative: boolean;
  /** True si l'utilisateur a coché "don récurrent" (subscription). */
  isSubscription: boolean;
  /**
   * Mode "wallets PayPal" : si vrai, on rend les 2 containers PayPal
   * Apple/Google Pay au lieu du Stripe Express Checkout. Vrai quand
   * preferred=paypal (les 2 actifs) OU PayPal-only.
   * Default: false (rétrocompat /c et /donate avant refactor).
   */
  usesPaypalWallets?: boolean;
  /** Override de l'ID DOM du container PayPal Smart Button. */
  paypalContainerId?: string;
  /** Override de l'ID DOM du container Apple Pay PayPal. */
  paypalApplePayContainerId?: string;
  /** Override de l'ID DOM du container Google Pay PayPal. */
  paypalGooglePayContainerId?: string;
}

const props = withDefaults(defineProps<Props>(), {
  usesPaypalWallets: false,
  paypalContainerId: undefined,
  paypalApplePayContainerId: "paypal-applepay-container",
  paypalGooglePayContainerId: "paypal-googlepay-container",
});

const shouldRender = computed(
  () => !props.isSubscription || props.isPaypalOnlyMode,
);

/**
 * Wallets Stripe (Apple+Google groupés) — visible UNIQUEMENT quand le
 * provider primaire est Stripe (preferred=stripe ou Stripe-only).
 */
const showStripeWallets = computed(
  () => !props.isPaypalOnlyMode && !props.usesPaypalWallets,
);

/**
 * Wallets PayPal (Apple+Google séparés) — visible quand preferred=paypal
 * (les 2 actifs avec preferred PayPal) OU mode PayPal-only.
 */
const showPaypalWallets = computed(
  () => props.usesPaypalWallets || props.isPaypalOnlyMode,
);

/** Bouton PayPal jaune (smart button). */
const showPaypalButton = computed(
  () => props.showPayPalAlternative || props.isPaypalOnlyMode,
);

const stripeWalletsReady = computed(
  () => props.stripeCheckout.expressCheckoutReady.value,
);
</script>

<template>
  <div
    v-if="shouldRender"
    class="relative mb-3 min-h-[48px]"
  >
    <div class="space-y-2">
      <!-- ───── Wallets Stripe (Apple+Google groupés) ───── -->
      <template v-if="showStripeWallets">
        <div v-if="!stripeWalletsReady" class="flex gap-2">
          <div class="flex-1 h-[44px] bg-gray-200 rounded-md animate-pulse" />
          <div class="flex-1 h-[44px] bg-gray-200 rounded-md animate-pulse" />
        </div>
        <div
          v-show="stripeWalletsReady"
          id="express-checkout-element"
          class="w-full select-none"
          style="-webkit-user-select: none; -webkit-touch-callout: none; -webkit-tap-highlight-color: transparent;"
        />
      </template>

      <!-- ───── Wallets PayPal (Apple+Google séparés) ─────
           Le SDK PayPal n'a pas d'API "express checkout combiné" : chaque
           wallet a son builder (paypal.Applepay() / paypal.Googlepay()) et
           son container DOM. Si un wallet n'est pas éligible (device pas
           Apple, capability non activée), le mount throw NOT_ELIGIBLE et
           on cache silencieusement le container. -->
      <template v-if="showPaypalWallets">
        <div
          :id="paypalApplePayContainerId"
          class="w-full min-h-[44px]"
        />
        <div
          :id="paypalGooglePayContainerId"
          class="w-full min-h-[44px]"
        />
      </template>

      <!-- ───── Bouton PayPal jaune (smart button) ─────
           - showPayPalAlternative : Stripe + PayPal → PayPal en complément
           - isPaypalOnlyMode : PayPal seul → PayPal remplace Stripe -->
      <PayPalAlternativeButton
        v-if="showPaypalButton"
        :paypal-checkout="paypalCheckout"
        :payment-form="paymentForm"
        :container-id="paypalContainerId"
      />
    </div>

  </div>
</template>
